import React, { useState, useEffect, useRef } from 'react'
import { Modal, Form, Button, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap'
import * as FaIcons from 'react-icons/fa'
import Select from 'react-select'

import { getRutas, getGuiasRuta, searchUsuariosMoviles as getRutasUsuarioMovil } from '@shared/helpers/api/mantenedores/rutas'
import { getExtras, createAsignacion } from '@shared/helpers/api/mantenedores/extras'
import { searchUsuariosMoviles } from '@shared/helpers/api/mantenedores/directorio'

const AsignacionModal = ({ show, onHide, onAsignacionCreada, user }) => {
    // Estados principales
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [step, setStep] = useState(1) // 1: Selección, 2: Formulario

    // Estados para conductores/rutas
    const [conductorSearchValue, setConductorSearchValue] = useState('')
    const [conductorOptions, setConductorOptions] = useState([])
    const [conductorSelected, setConductorSelected] = useState(null)
    const [loadingConductores, setLoadingConductores] = useState(false)

    // Estados para rutas
    const [incluirRuta, setIncluirRuta] = useState(false)
    const [rutaOptions, setRutaOptions] = useState([])
    const [rutaSelected, setRutaSelected] = useState(null)
    const [loadingRutas, setLoadingRutas] = useState(false)

    // Estados para extras
    const [extraOptions, setExtraOptions] = useState([])
    const [extraSelected, setExtraSelected] = useState(null)
    const [loadingExtras, setLoadingExtras] = useState(false)

    // Estados para guías
    const [incluirGuia, setIncluirGuia] = useState(false)
    const [guiaOptions, setGuiaOptions] = useState([])
    const [guiaSelected, setGuiaSelected] = useState(null)
    const [loadingGuias, setLoadingGuias] = useState(false)

    // Estados del formulario
    const [formData, setFormData] = useState({
        detalle: '',
        fecha_asignacion: new Date().toISOString().split('T')[0], // Formato yyyy-mm-dd
        cantidad: 1,
        monto: ''
    })

    // Constante para el límite de caracteres del detalle
    const MAX_DETALLE_CHARS = 1000

    // Refs para timeouts
    const conductorSearchTimeout = useRef(null)

    // Cargar extras al abrir modal
    useEffect(() => {
        if (show) {
            loadExtras()
            resetModal()
        }
    }, [show])

    const resetModal = () => {
        setStep(1)
        setError(null)
        setConductorSearchValue('')
        setConductorOptions([])
        setConductorSelected(null)
        setIncluirRuta(false)
        setRutaOptions([])
        setRutaSelected(null)
        setExtraSelected(null)
        setIncluirGuia(false)
        setGuiaSelected(null)
        setGuiaOptions([])
        setFormData({
            detalle: '',
            fecha_asignacion: new Date().toISOString().split('T')[0], // Formato yyyy-mm-dd
            cantidad: 1,
            monto: ''
        })
    }

    // Búsqueda de conductores con debounce
    const searchConductores = async searchValue => {
        if (!searchValue || searchValue.length < 2) {
            setConductorOptions([])
            return
        }

        setLoadingConductores(true)
        try {
            const response = await searchUsuariosMoviles(user, searchValue)

            if (response.success) {
                const options = response.data.map(usuario => ({
                    value: usuario.id, // Cambiado de usuario.usuario_movil_id a usuario.id
                    label: usuario.usuarioSpread,
                    data: usuario
                }))
                setConductorOptions(options)
            }
        } catch (err) {
            console.error('Error al buscar conductores:', err)
        } finally {
            setLoadingConductores(false)
        }
    }

    // Handle conductor search con debounce
    const handleConductorInputChange = inputValue => {
        setConductorSearchValue(inputValue)

        if (conductorSearchTimeout.current) {
            clearTimeout(conductorSearchTimeout.current)
        }

        conductorSearchTimeout.current = setTimeout(() => {
            searchConductores(inputValue)
        }, 500)
    }

    // Cargar extras disponibles
    const loadExtras = async () => {
        setLoadingExtras(true)
        try {
            const response = await getExtras(user)
            if (response.success) {
                const options = response.data.map(extra => ({
                    value: extra.id,
                    label: `${extra.tipo_extra.tipo} - ${extra.extra}`,
                    data: extra
                }))
                setExtraOptions(options)
            }
        } catch (err) {
            console.error('Error al cargar extras:', err)
        } finally {
            setLoadingExtras(false)
        }
    }

    // Cargar guías de la ruta seleccionada
    const loadGuiasRuta = async rutaId => {
        setLoadingGuias(true)
        try {
            const response = await getGuiasRuta(user, rutaId)
            if (response.success) {
                const options = response.data.map(guia => ({
                    value: guia.id,
                    label: `${guia.guia} - ${guia.cliente?.cliente || 'Sin cliente'}`,
                    data: guia
                }))
                setGuiaOptions(options)
            }
        } catch (err) {
            console.error('Error al cargar guías:', err)
            setError('Error al cargar guías de la ruta')
        } finally {
            setLoadingGuias(false)
        }
    }

    // Handlers
    const handleConductorChange = selectedOption => {
        setConductorSelected(selectedOption)
        setRutaSelected(null)
        setRutaOptions([])
        setGuiaSelected(null)
        setGuiaOptions([])
        
        // Ya no preparamos las rutas automáticamente aquí
        // Las rutas se cargarán solo cuando se active el switch de incluir ruta
    }

    const handleExtraChange = selectedOption => {
        setExtraSelected(selectedOption)
        // Auto-completar monto si el extra tiene monto base
        if (selectedOption?.data?.monto) {
            setFormData(prev => ({
                ...prev,
                monto: selectedOption.data.monto
            }))
        }
    }

    // Cargar rutas del usuario móvil seleccionado
    const loadRutasUsuarioMovil = async (usuarioMovilId) => {
        setLoadingRutas(true)
        try {
            const response = await getRutasUsuarioMovil(user, usuarioMovilId)

            if (response.success && response.data.length > 0) {
                const usuario = response.data[0] // Debería ser solo un usuario
                if (usuario.rutas && usuario.rutas.length > 0) {
                    const rutaOpts = usuario.rutas
                        .sort((a, b) => new Date(b.fecha) - new Date(a.fecha)) // Ordenar por fecha más nueva primero
                        .map(ruta => ({
                            value: ruta.id,
                            label: `${ruta.ruta} / ${ruta.fecha}`,
                            data: ruta
                        }))
                    setRutaOptions(rutaOpts)
                } else {
                    setRutaOptions([])
                }
            } else {
                setRutaOptions([])
            }
        } catch (err) {
            console.error('Error al cargar rutas del usuario móvil:', err)
            setRutaOptions([])
        } finally {
            setLoadingRutas(false)
        }
    }

    const handleIncluirRutaChange = checked => {
        setIncluirRuta(checked)
        setRutaSelected(null)
        setGuiaSelected(null)
        setGuiaOptions([])
        
        // Cargar rutas si se activa el switch y hay un conductor seleccionado
        if (checked && conductorSelected?.value) {
            loadRutasUsuarioMovil(conductorSelected.value)
        } else {
            setRutaOptions([])
        }
    }

    const handleRutaChange = selectedOption => {
        setRutaSelected(selectedOption)
        setGuiaSelected(null)
        setGuiaOptions([])
    }

    const handleIncluirGuiaChange = checked => {
        setIncluirGuia(checked)
        setGuiaSelected(null)

        if (checked && rutaSelected) {
            loadGuiasRuta(rutaSelected.value)
        }
    }

    const handleFormChange = (field, value) => {
        // Validar límite de caracteres para el campo detalle
        if (field === 'detalle' && value.length > MAX_DETALLE_CHARS) {
            return // No permitir escribir más allá del límite
        }
        
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const handleContinuar = () => {
        if (!conductorSelected || !extraSelected) {
            setError('Debe seleccionar un conductor y un extra')
            return
        }

        if (incluirRuta && !rutaSelected) {
            setError('Debe seleccionar una ruta o desactivar la opción')
            return
        }

        if (incluirGuia && !guiaSelected) {
            setError('Debe seleccionar una guía o desactivar la opción')
            return
        }

        setError(null)
        setStep(2)
    }

    const handleVolver = () => {
        setStep(1)
        setError(null)
    }

    const handleSubmit = async () => {
        // Validaciones
        if (!formData.detalle.trim()) {
            setError('El detalle es obligatorio')
            return
        }

        if (!formData.monto || formData.monto <= 0) {
            setError('El monto debe ser mayor a 0')
            return
        }

        setLoading(true)
        setError(null)
        try {
            const asignacionData = {
                extras_id: extraSelected.value,
                usuario_movil_id: conductorSelected.value,
                ruta_id: incluirRuta ? rutaSelected?.value : null,
                guia_id: incluirGuia ? guiaSelected?.value : null,
                fecha: formData.fecha_asignacion, // Formato yyyy-mm-dd
                detalle: formData.detalle.trim(),
                cantidad: parseInt(formData.cantidad),
                monto: parseFloat(formData.monto)
            }
            

            const response = await createAsignacion(user, asignacionData)

            if (response.success) {
                onAsignacionCreada()
            } else {
                setError(response.error)
            }
        } catch (err) {
            console.error('Error al crear asignación:', err)
            setError('Error al crear la asignación')
        } finally {
            setLoading(false)
        }
    }

    const formatearMonto = monto => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(monto)
    }

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaIcons.FaUserPlus className="me-2" style={{ color: '#00b894' }} />
                    Nueva Asignación de Extra
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        <FaIcons.FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}

                {step === 1 && (
                    <>
                        <h6 className="mb-3">Paso 1: Seleccionar Conductor y Extra</h6>

                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500' }}>
                                        <FaIcons.FaSearch className="me-2" />
                                        Buscar Conductor
                                    </Form.Label>
                                    <Select
                                        isSearchable
                                        isClearable
                                        isLoading={loadingConductores}
                                        placeholder="Escriba para buscar conductor..."
                                        options={conductorOptions}
                                        value={conductorSelected}
                                        onChange={handleConductorChange}
                                        onInputChange={handleConductorInputChange}
                                        noOptionsMessage={() =>
                                            conductorSearchValue.length < 2
                                                ? 'Escriba al menos 2 caracteres...'
                                                : 'No se encontraron conductores'
                                        }
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500' }}>
                                        <FaIcons.FaGift className="me-2" />
                                        Seleccionar Extra
                                    </Form.Label>
                                    <Select
                                        isSearchable
                                        isClearable
                                        isLoading={loadingExtras}
                                        placeholder="Seleccione un extra..."
                                        options={extraOptions}
                                        value={extraSelected}
                                        onChange={handleExtraChange}
                                        noOptionsMessage={() => 'No se encontraron extras'}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={12}>
                                <Form.Check
                                    type="switch"
                                    id="incluir-ruta-switch"
                                    label="Asignar a ruta específica"
                                    checked={incluirRuta}
                                    onChange={e => handleIncluirRutaChange(e.target.checked)}
                                    disabled={!conductorSelected}
                                />
                            </Col>

                            {incluirRuta && (
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '500' }}>
                                            <FaIcons.FaRoute className="me-2" />
                                            Seleccionar Ruta
                                        </Form.Label>
                                        <Select
                                            isSearchable
                                            isClearable
                                            isLoading={loadingRutas}
                                            placeholder={loadingRutas ? "Cargando rutas..." : "Seleccione una ruta..."}
                                            options={rutaOptions}
                                            value={rutaSelected}
                                            onChange={handleRutaChange}
                                            noOptionsMessage={() => loadingRutas ? 'Cargando...' : 'No se encontraron rutas'}
                                        />
                                    </Form.Group>
                                </Col>
                            )}

                            <Col xs={12}>
                                <Form.Check
                                    type="switch"
                                    id="incluir-guia-switch"
                                    label="Asignar a guía específica"
                                    checked={incluirGuia}
                                    onChange={e => handleIncluirGuiaChange(e.target.checked)}
                                    disabled={!incluirRuta || !rutaSelected}
                                />
                            </Col>

                            {incluirGuia && (
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label style={{ fontWeight: '500' }}>
                                            <FaIcons.FaFileAlt className="me-2" />
                                            Seleccionar Guía
                                        </Form.Label>
                                        <Select
                                            isSearchable
                                            isClearable
                                            isLoading={loadingGuias}
                                            placeholder="Seleccione una guía..."
                                            options={guiaOptions}
                                            value={guiaSelected}
                                            onChange={setGuiaSelected}
                                            noOptionsMessage={() => 'No se encontraron guías'}
                                        />
                                    </Form.Group>
                                </Col>
                            )}
                        </Row>
                    </>
                )}

                {step === 2 && (
                    <>
                        <h6 className="mb-3">Paso 2: Completar Información</h6>

                        {/* Resumen de selección */}
                        <div className="bg-light p-3 rounded mb-3">
                            <h6 className="mb-2">Resumen de Selección:</h6>
                            <p className="mb-1">
                                <strong>Conductor:</strong> {conductorSelected?.label}
                            </p>
                            {conductorSelected?.data?.rutas && (
                                <p className="mb-1">
                                    <strong>Total de rutas disponibles:</strong> {conductorSelected.data.rutas.length}
                                </p>
                            )}
                            {incluirRuta && rutaSelected && (
                                <p className="mb-1">
                                    <strong>Ruta seleccionada:</strong> {rutaSelected.label}
                                </p>
                            )}
                            <p className="mb-1">
                                <strong>Extra:</strong> {extraSelected?.label}
                            </p>
                            {incluirGuia && guiaSelected && (
                                <p className="mb-0">
                                    <strong>Guía:</strong> {guiaSelected.label}
                                </p>
                            )}
                        </div>

                        <Row className="g-3">
                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500' }}>
                                        Detalle *
                                        <span className={`ms-2 ${formData.detalle.length > MAX_DETALLE_CHARS * 0.9 ? 'text-warning' : 'text-muted'}`}>
                                            ({formData.detalle.length}/{MAX_DETALLE_CHARS})
                                        </span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Describa el motivo de este extra..."
                                        value={formData.detalle}
                                        onChange={e => handleFormChange('detalle', e.target.value)}
                                        maxLength={MAX_DETALLE_CHARS}
                                        className={formData.detalle.length >= MAX_DETALLE_CHARS ? 'border-warning' : ''}
                                    />
                                    {formData.detalle.length > MAX_DETALLE_CHARS * 0.9 && (
                                        <Form.Text className="text-warning">
                                            <FaIcons.FaExclamationTriangle className="me-1" />
                                            Se está acercando al límite de caracteres
                                        </Form.Text>
                                    )}
                                    {formData.detalle.length >= MAX_DETALLE_CHARS && (
                                        <Form.Text className="text-danger">
                                            <FaIcons.FaExclamationTriangle className="me-1" />
                                            Ha alcanzado el límite máximo de caracteres
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col xs={12} md={6}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500' }}>Fecha de Asignación *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={formData.fecha_asignacion}
                                        onChange={e => handleFormChange('fecha_asignacion', e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Fecha en la que se aplicará este extra al conductor
                                    </Form.Text>
                                </Form.Group>
                            </Col>

                            <Col xs={12} md={6}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500' }}>Cantidad *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="1"
                                        value={formData.cantidad}
                                        onChange={e => handleFormChange('cantidad', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col xs={12}>
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500' }}>
                                        Monto *
                                        {extraSelected?.data?.monto && (
                                            <Badge bg="info" className="ms-2">
                                                Sugerido: {formatearMonto(extraSelected.data.monto)}
                                            </Badge>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={formData.monto}
                                        onChange={e => handleFormChange('monto', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Cancelar
                </Button>

                {step === 1 && (
                    <Button
                        variant="primary"
                        onClick={handleContinuar}
                        disabled={
                            !conductorSelected || 
                            !extraSelected || 
                            (incluirRuta && !rutaSelected) ||
                            (incluirGuia && !guiaSelected)
                        }>
                        <FaIcons.FaArrowRight className="me-2" />
                        Continuar
                    </Button>
                )}

                {step === 2 && (
                    <>
                        <Button variant="outline-secondary" onClick={handleVolver} disabled={loading}>
                            <FaIcons.FaArrowLeft className="me-2" />
                            Volver
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleSubmit}
                            disabled={loading || !formData.detalle.trim() || !formData.monto || !formData.fecha_asignacion}>
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <FaIcons.FaSave className="me-2" />
                                    Guardar Asignación
                                </>
                            )}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default AsignacionModal
